import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { emiAPI } from '../services/api';
import CollectEmiModal from '../components/branch/CollectEmiModal';
import { formatCurrency as fmt } from '../utils/format';
const daysBetween = (a, b) => Math.floor((b - a) / (1000 * 60 * 60 * 24));

export default function EmiCollections() {
  const [searchParams] = useSearchParams();
  const highlightEmiId = searchParams.get('emiId') || '';
  const highlightRef = useRef(null);
  const [emis, setEmis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collectEmi, setCollectEmi] = useState(null);
  const [remindingId, setRemindingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [highlightApplied, setHighlightApplied] = useState(false);

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line

  useEffect(() => {
    setHighlightApplied(false);
  }, [highlightEmiId]);

  useEffect(() => {
    if (!highlightEmiId || !emis.length || highlightApplied) return;
    const match = emis.find((e) => String(e._id) === highlightEmiId);
    if (match) {
      if (match.status && match.status !== statusFilter) {
        setStatusFilter(match.status);
        return;
      }
      setSearch('');
      setHighlightApplied(true);
      requestAnimationFrame(() => {
        highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    } else if (statusFilter !== '') {
      setStatusFilter('');
    }
  }, [highlightEmiId, emis, highlightApplied, statusFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const res = await emiAPI.list(params);
      setEmis(res.data.emis || []);
    } finally {
      setLoading(false);
    }
  };

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return emis;
    return emis.filter(e =>
      (e.customerName || '').toLowerCase().includes(q) ||
      (e.customerPhone || '').toLowerCase().includes(q) ||
      (e.productName || '').toLowerCase().includes(q)
    );
  }, [emis, search]);

  const now = new Date();

  const counts = useMemo(() => ({
    active: emis.filter(e => e.status === 'active').length,
    overdue: emis.filter(e => e.status === 'active' && e.nextDue && new Date(e.nextDue) < now).length,
    dueSoon: emis.filter(e => {
      if (e.status !== 'active' || !e.nextDue) return false;
      const d = new Date(e.nextDue);
      return d >= now && daysBetween(now, d) <= 7;
    }).length
  }), [emis]); // eslint-disable-line

  const remind = async (emi) => {
    setRemindingId(emi._id);
    try {
      const res = await emiAPI.remind(emi._id);
      if (res.data?.sent) flash(res.data?.overdue ? 'Overdue reminder sent on WhatsApp.' : 'Reminder sent on WhatsApp.');
      else flash('Reminder could not be delivered. Check customer phone.');
    } catch (e) {
      flash(e.response?.data?.message || 'Reminder failed');
    } finally {
      setRemindingId(null);
    }
  };

  const remindBulk = async (type) => {
    const label = type === 'overdue' ? 'overdue customers' : 'customers due in 7 days';
    if (!window.confirm(`Send WhatsApp reminders to all ${label}?`)) return;
    setBulkBusy(true);
    try {
      const payload = type === 'overdue' ? { onlyOverdue: true } : { onlyDueSoon: true };
      const res = await emiAPI.remindBulk(payload);
      flash(`Reminders: ${res.data.sent} sent, ${res.data.failed} failed`);
    } catch (e) {
      flash(e.response?.data?.message || 'Bulk reminder failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const statusBadge = (s) => {
    if (s === 'completed') return 'chip badge-completed';
    if (s === 'overdue')   return 'chip badge-overdue';
    return 'chip badge-active';
  };

  return (
    <div>
      {/* ── Header ── */}
      <div className="d-flex flex-wrap justify-content-between align-items-start mb-3">
        <div>
          <h4 className="mb-0 fw-700">EMI Collections</h4>
          <div className="text-muted" style={{ fontSize: 13 }}>
            Recovery view · KYC and witness visible at a glance
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap mt-2 mt-sm-0">
          {counts.overdue > 0 && (
            <button className="btn btn-sm btn-danger" onClick={() => remindBulk('overdue')} disabled={bulkBusy}>
              Remind all overdue ({counts.overdue})
            </button>
          )}
          {counts.dueSoon > 0 && (
            <button className="btn btn-sm btn-outline-warning" onClick={() => remindBulk('dueSoon')} disabled={bulkBusy}>
              Remind due in 7 days ({counts.dueSoon})
            </button>
          )}
          <button className="btn btn-sm btn-outline-secondary" onClick={load}>↻ Refresh</button>
        </div>
      </div>

      {/* ── Summary chips ── */}
      <div className="row g-2 mb-3">
        <div className="col-6 col-md-3">
          <div className="stat-tile" style={{ borderLeft: '4px solid #0d6efd', padding: 12 }}>
            <div className="stat-label">Active accounts</div>
            <div className="stat-value" style={{ color: '#0d6efd', fontSize: 20 }}>{counts.active}</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="stat-tile" style={{ borderLeft: `4px solid ${counts.overdue > 0 ? '#b91c1c' : '#e5e7eb'}`, padding: 12 }}>
            <div className="stat-label">Overdue</div>
            <div className="stat-value" style={{ color: counts.overdue > 0 ? '#b91c1c' : '#6b7280', fontSize: 20 }}>{counts.overdue}</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="stat-tile" style={{ borderLeft: '4px solid #b45309', padding: 12 }}>
            <div className="stat-label">Due in 7 days</div>
            <div className="stat-value" style={{ color: '#b45309', fontSize: 20 }}>{counts.dueSoon}</div>
          </div>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <div className="btn-group">
          {[
            { k: 'active',    label: `Active (${counts.active})` },
            { k: 'completed', label: 'Completed' },
            { k: 'defaulted', label: 'Defaulted' }
          ].map(s => (
            <button
              key={s.k}
              className={`btn btn-sm ${statusFilter === s.k ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setStatusFilter(s.k)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <input
          className="form-control"
          style={{ maxWidth: 280 }}
          placeholder="Search customer, product…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {toast && <div className="alert-soft success mb-3">{toast}</div>}
      {highlightEmiId && highlightApplied && (
        <div className="alert-soft success mb-3" style={{ fontSize: 13 }}>
          Customer profile se linked EMI highlighted — neeche scroll karein.
        </div>
      )}

      {/* ── Table ── */}
      <div className="table-card">
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Product</th>
                <th>Progress</th>
                <th>Next due</th>
                <th>KYC / Witness</th>
                <th>Status</th>
                <th style={{ width: 180 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="7" className="text-center py-3">Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan="7" className="text-center text-muted py-4">No EMIs found.</td></tr>
              )}
              {filtered.map(emi => {
                const due = emi.nextDue ? new Date(emi.nextDue) : null;
                const overdueDays = due ? Math.max(0, daysBetween(due, now)) : 0;
                const isOverdue = overdueDays > 0;
                const isHighlighted = highlightEmiId && String(emi._id) === highlightEmiId;
                return (
                  <tr
                    key={emi._id}
                    id={isHighlighted ? `emi-row-${emi._id}` : undefined}
                    ref={isHighlighted ? highlightRef : null}
                    style={
                      isHighlighted
                        ? { background: '#eff6ff', boxShadow: 'inset 0 0 0 2px #2563eb' }
                        : isOverdue
                          ? { background: '#fff5f5' }
                          : undefined
                    }
                  >
                    <td>
                      <strong>{emi.customerName || '—'}</strong>
                      <div className="text-muted" style={{ fontSize: 12 }}>{emi.customerPhone || ''}</div>
                      {emi.linkedCustomerId && (
                        <Link to={`/customers/${emi.linkedCustomerId}`} className="small text-primary">
                          View profile →
                        </Link>
                      )}
                    </td>
                    <td>
                      {emi.productName}
                      <div className="text-muted" style={{ fontSize: 11 }}>{emi.category || ''}</div>
                    </td>
                    <td>
                      <div style={{ fontSize: 13 }}>{emi.paidMonths}/{emi.totalMonths} paid</div>
                      <div className="progress" style={{ height: 4, borderRadius: 4, marginTop: 4, background: '#e5e7eb', width: 80 }}>
                        <div
                          className="progress-bar bg-primary"
                          style={{ width: `${emi.totalMonths ? Math.round((emi.paidMonths/emi.totalMonths)*100) : 0}%`, borderRadius: 4 }}
                        />
                      </div>
                      <div className="text-muted" style={{ fontSize: 11 }}>{fmt(emi.nextAmount)}/mo</div>
                    </td>
                    <td>
                      {due ? due.toLocaleDateString('en-IN') : '—'}
                      {isOverdue && (
                        <div className="chip badge-overdue mt-1" style={{ fontSize: 10 }}>
                          {overdueDays}d overdue
                        </div>
                      )}
                    </td>
                    <td>
                      {emi.kycComplete
                        ? <span className="chip badge-kyc-complete">KYC ✓</span>
                        : <span className="chip badge-kyc-pending">KYC ✗</span>}
                      {emi.witnessOnFile
                        ? <span className="chip badge-witness ms-1">W ✓</span>
                        : <span className="chip badge-kyc-pending ms-1">No W</span>}
                    </td>
                    <td><span className={statusBadge(emi.status)}>{emi.status}</span></td>
                    <td>
                      <div className="d-flex gap-1">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          disabled={remindingId === emi._id || emi.status !== 'active'}
                          onClick={() => remind(emi)}
                          title="Send WhatsApp reminder"
                        >
                          {remindingId === emi._id ? '…' : '📲 Remind'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-success"
                          disabled={emi.status !== 'active'}
                          onClick={() => setCollectEmi(emi)}
                        >
                          Collect
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Collect modal ── */}
      {collectEmi && (
        <CollectEmiModal
          emi={collectEmi}
          onClose={() => setCollectEmi(null)}
          onSuccess={(msg) => {
            setCollectEmi(null);
            flash(msg);
            load();
          }}
        />
      )}
    </div>
  );
}
